import React from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { Status } from '../../../../types/redux/data/dataTypes';
import { percentageToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import { specifyStatus } from '../../../../utilities/generalMappings';
import type { CssfRiskSpreadingPayload } from '../../../../api/rs-hg/risk/cssfRiskSpreading.types';
import type { RuleTimeSeriesPoint } from '../../../../api/rs-hg/risk/ruleTimeSeries.types';
import RuleTimeSeriesCell from '../../risk/shared/RuleTimeSeriesCell';

export interface CssfTableData {
  number: number;
  ruleTitle: string;
  value: number;
  limit: number;
  status: Status;
  historical_time_series?: RuleTimeSeriesPoint[];
}

export const generateCssfColumns = (
  fundId: string,
): CustomColumn<CssfTableData>[] => [
  {
    field: 'number',
    title: '#',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
    },
    width: '1%',
  },
  {
    field: 'ruleTitle',
    title: 'Rule',
    headerStyle: {
      textAlign: 'left',
    },
    cellStyle: {
      textAlign: 'left',
    },
  },
  {
    field: 'value',
    title: 'Value',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
    },
    width: '9.38rem',
    render: (rowData) => percentageToTwoDecimalPlaces(rowData.value),
  },
  {
    field: 'limit',
    title: 'Limit',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
    },
    render: (rowData) => percentageToTwoDecimalPlaces(rowData.limit),
    width: '9.38rem',
  },
  {
    field: 'status',
    title: 'Status',
    pdfRenderType: 'PageLinkButton',
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      textAlign: 'center',
    },
    width: '9.38rem',
    render: (rowData: CssfTableData) => (
      <RaptorStatusBox status={specifyStatus(rowData.status)} />
    ),
  },
  {
    title: 'Time Series',
    sorting: false,
    headerStyle: {
      textAlign: 'center',
    },
    cellStyle: {
      padding: '0.62rem 0',
      textAlign: 'center',
      width: '40%',
    },
    render: (rowData: CssfTableData) => (
      <RuleTimeSeriesCell
        timeSeriesKey="cssf"
        ruleParams={{ fundName: fundId, ruleName: rowData.ruleTitle }}
        chartId={`${fundId}-${rowData.ruleTitle}`}
        enabled={!!fundId && !!rowData.ruleTitle}
        initialPoints={
          rowData.historical_time_series?.length
            ? rowData.historical_time_series
            : undefined
        }
        formatter={percentageToTwoDecimalPlaces}
      />
    ),
  },
];

export const generateCssfData = (
  payload: CssfRiskSpreadingPayload | null | undefined,
): CssfTableData[] => {
  if (!payload?.cssf_list?.length) return [];

  // don't include the header
  const seriesByRule = payload.time_series_by_rule ?? {};
  return payload.cssf_list.slice(1).map((el, index) => {
    const [ruleTitle, status, value, limit] = el;
    const name = String(ruleTitle);
    return {
      number: index + 1,
      ruleTitle: name,
      value: Number(value),
      limit: Number(limit),
      status: specifyStatus(String(status)),
      historical_time_series: seriesByRule[name]?.length
        ? seriesByRule[name]
        : undefined,
    };
  });
};
